import React, { useState } from 'react';
import CallNotes from './CallNotes';
import DealForm from './DealForm';
import DeckUpload from './DeckUpload';
import MemoOutput from './MemoOutput';
import ThesisEditor, { useStoredThesis } from './ThesisEditor';
import { CallNote, DealBasics, Deck } from './types';
import styles from './MemoBuilder.module.css';

const EMPTY_DEAL: DealBasics = {
  companyName: '',
  stage: 'Seed',
  roundSize: '',
  valuation: '',
  valuationBasis: 'post-money',
  leadInvestor: '',
  dealOwner: '',
};

interface SectionProps {
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ step, title, description, children }) => (
  <section className={styles.card}>
    <header className={styles.cardHeader}>
      <span className={styles.step}>{step}</span>
      <div>
        <h2 className={styles.cardTitle}>{title}</h2>
        <p className={styles.cardDescription}>{description}</p>
      </div>
    </header>
    {children}
  </section>
);

const MemoBuilder: React.FC = () => {
  const [deal, setDeal] = useState<DealBasics>(EMPTY_DEAL);
  const [deck, setDeck] = useState<Deck | null>(null);
  const [notes, setNotes] = useState<CallNote[]>([]);
  const [thesis, setThesis] = useStoredThesis();

  const checklist = [
    { label: 'Deal basics', done: deal.companyName.trim() !== '' && deal.roundSize.trim() !== '' },
    { label: 'Pitch deck', done: deck !== null },
    { label: 'Partner call notes', done: notes.length > 0 },
    { label: 'Fund thesis', done: thesis.trim() !== '' },
  ];

  const hasCompany = deal.companyName.trim() !== '';
  const hasMaterial = deck !== null || notes.length > 0;
  const notReadyHint = !hasCompany
    ? 'Enter a company name to generate a memo.'
    : 'Add a pitch deck or call notes to generate a memo.';

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <a href="/" className={styles.backLink} aria-label="Back to blog">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Blog
        </a>
        <div className={styles.brand}>
          <span className={styles.brandFirm}>Origin Ventures</span>
          <span className={styles.brandDivider} aria-hidden>
            /
          </span>
          <span className={styles.brandProduct}>IC Memo Builder</span>
        </div>
      </header>

      <main className={styles.layout}>
        <div className={styles.inputs}>
          <Section step={1} title="Deal basics" description="The terms of the round under consideration.">
            <DealForm deal={deal} onChange={setDeal} />
          </Section>

          <Section
            step={2}
            title="Pitch deck"
            description="PDFs are read for text and rendered page by page so charts and metric slides are captured."
          >
            <DeckUpload deck={deck} onChange={setDeck} />
          </Section>

          <Section step={3} title="Partner call notes" description="Upload .txt files or paste notes. Date each call.">
            <CallNotes notes={notes} onChange={setNotes} />
          </Section>

          <Section step={4} title="Fund thesis" description="What the memo is evaluated against. Edits are remembered.">
            <ThesisEditor thesis={thesis} onChange={setThesis} />
          </Section>
        </div>

        <aside className={styles.output}>
          <div className={styles.outputCard}>
            <h2 className={styles.cardTitle}>
              {deal.companyName.trim() ? `${deal.companyName.trim()} IC memo` : 'IC memo'}
            </h2>
            <p className={styles.cardDescription}>
              {[deal.stage, deal.roundSize && `${deal.roundSize} round`, deal.valuation && `${deal.valuation} ${deal.valuationBasis}`]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <ul className={styles.checklist}>
              {checklist.map((item) => (
                <li key={item.label} className={item.done ? styles.checkDone : undefined}>
                  <span className={styles.checkMark} aria-hidden>
                    {item.done ? '✓' : ''}
                  </span>
                  {item.label}
                </li>
              ))}
            </ul>
            <MemoOutput
              inputs={{ deal, deck, notes, thesis }}
              ready={hasCompany && hasMaterial}
              notReadyHint={notReadyHint}
            />
          </div>
        </aside>
      </main>
    </div>
  );
};

export default MemoBuilder;
